import { useState, useEffect } from "react";
import api, { formatBRL } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { Plus, Trash2, Upload, Camera, Pencil } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { initials, resolveAvatar } from "@/lib/api";
import { UfCursoSelect } from "@/components/UfCursoSelect";

function UsuariosTab() {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [nucleos, setNucleos] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState({ nome: "", email: "", password: "", role: "vendedora", nucleo_id: "", codigo: "" });
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(null); // { id, nome, email, password, role, nucleo_id }

  const load = () => { api.get("/users").then((r) => setUsers(r.data)); };
  useEffect(() => {
    load();
    api.get("/nucleos").then((r) => setNucleos(r.data));
  }, []);

  const salvar = async () => {
    try {
      const payload = { ...form, nucleo_id: form.nucleo_id || null };
      // Só envia codigo se preenchido (senão o backend auto-gera V00N para vendedoras)
      if (!payload.codigo || !payload.codigo.trim()) delete payload.codigo;
      await api.post("/users", payload);
      toast.success("Usuário criado");
      setDlg(false); setForm({ nome: "", email: "", password: "", role: "vendedora", nucleo_id: "", codigo: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const abrirEdicao = (u) => {
    setEditForm({
      id: u.id,
      nome: u.nome || "",
      email: u.email || "",
      password: "", // vazio = não altera
      role: u.role || "vendedora",
      nucleo_id: u.nucleo_id || "",
      codigo: u.codigo || "",
    });
    setEditOpen(true);
  };

  const salvarEdicao = async () => {
    if (!editForm?.id) return;
    // Só envia campos preenchidos (password vazio significa "não alterar")
    const payload = {
      nome: editForm.nome,
      email: editForm.email,
      role: editForm.role,
      nucleo_id: editForm.nucleo_id && editForm.nucleo_id !== "none" ? editForm.nucleo_id : null,
      codigo: (editForm.codigo || "").trim() || null,
    };
    if (editForm.password && editForm.password.trim().length > 0) {
      if (editForm.password.length < 6) {
        toast.error("Nova senha deve ter no mínimo 6 caracteres.");
        return;
      }
      payload.password = editForm.password;
    }
    try {
      await api.patch(`/users/${editForm.id}`, payload);
      toast.success("Usuário atualizado");
      setEditOpen(false); setEditForm(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao atualizar"); }
  };

  const excluir = async (id) => {
    if (!window.confirm("Excluir usuário?")) return;
    try { await api.delete(`/users/${id}`); load(); } catch { toast.error("Erro"); }
  };

  const uploadAvatar = async (uid, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/users/${uid}/avatar`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Foto atualizada");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro ao enviar foto"); }
  };

  return (
    <Card className="p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl">Usuários</h2>
        <Button onClick={() => setDlg(true)} data-testid="btn-novo-usuario"><Plus className="w-4 h-4 mr-1" /> Novo</Button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
            <th className="pb-3 pr-3 w-14">Foto</th>
            <th className="pb-3 pr-3">Nome</th><th className="pb-3 pr-3">E-mail</th>
            <th className="pb-3 pr-3">Papel</th><th className="pb-3 pr-3">Núcleo</th>
            <th className="pb-3"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => (
            <tr key={u.id} className="border-b border-slate-100">
              <td className="py-2 pr-3">
                <label className="cursor-pointer group inline-block relative" title="Enviar foto">
                  <Avatar className="w-9 h-9">
                    <AvatarImage src={resolveAvatar(u.avatar_url)} alt={u.nome} />
                    <AvatarFallback className="text-xs bg-slate-100 text-slate-700 font-bold">
                      {initials(u.nome)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute inset-0 rounded-full bg-black/50 opacity-0 group-hover:opacity-100 flex items-center justify-center transition">
                    <Camera className="w-4 h-4 text-white" />
                  </span>
                  <input
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => uploadAvatar(u.id, e.target.files?.[0])}
                    data-testid={`upload-avatar-${u.id}`}
                  />
                </label>
              </td>
              <td className="py-2 pr-3 font-medium">
                {u.nome}
                {u.codigo && <span className="ml-2 text-xs bg-blue-50 text-blue-700 rounded px-1.5 py-0.5 font-bold">{u.codigo}</span>}
              </td>
              <td className="py-2 pr-3 text-slate-500">{u.email}</td>
              <td className="py-2 pr-3">{u.role}</td>
              <td className="py-2 pr-3">{u.nucleo_nome || "—"}</td>
              <td className="py-2">
                <div className="flex items-center gap-2 justify-end">
                  {user?.role === "diretoria" && (
                    <button
                      onClick={() => abrirEdicao(u)}
                      className="text-blue-600 hover:text-blue-800"
                      title="Editar usuário"
                      data-testid={`btn-editar-usuario-${u.id}`}
                    >
                      <Pencil className="w-4 h-4" />
                    </button>
                  )}
                  {user?.role === "diretoria" && u.id !== user.id && (
                    <button
                      onClick={() => excluir(u.id)}
                      className="text-red-500 hover:text-red-700"
                      title="Excluir usuário"
                      data-testid={`btn-excluir-usuario-${u.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent>
          <DialogHeader><DialogTitle>Novo usuário</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="user-nome" /></div>
            <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email" /></div>
            <div><Label>Senha</Label><Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-password" /></div>
            <div>
              <Label>Papel</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="vendedora">Vendedora</SelectItem>
                  <SelectItem value="supervisao">Supervisão</SelectItem>
                  <SelectItem value="diretoria">Diretoria</SelectItem>
                  <SelectItem value="fiscal">Fiscal</SelectItem>
                  <SelectItem value="analista_mkt">Analista de MKT</SelectItem>
                  <SelectItem value="gestao_cel">Gestão CeL Consultoria</SelectItem>
                  <SelectItem value="indicador">Indicador (parceiro externo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Núcleo</Label>
              <Select value={form.nucleo_id} onValueChange={(v) => setForm({ ...form, nucleo_id: v })}>
                <SelectTrigger><SelectValue placeholder="Selecionar" /></SelectTrigger>
                <SelectContent>
                  {nucleos.map((n) => (<SelectItem key={n.id} value={n.id}>{n.nome}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-xs text-slate-500 italic">
              💡 A meta de receita é definida mensalmente na aba <b>Metas Mensais</b>.
              Para Diretoria/Supervisão, a meta é agregada automaticamente pelas metas das vendedoras.
            </p>
            <div>
              <Label>Código da vendedora <span className="text-slate-400 text-xs">(opcional — se vazio, o sistema gera V001, V002...)</span></Label>
              <Input
                value={form.codigo}
                onChange={(e) => setForm({ ...form, codigo: e.target.value })}
                placeholder="ex: V001 ou 1"
                data-testid="user-codigo"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button onClick={salvar} data-testid="user-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editar usuário — Diretoria */}
      <Dialog open={editOpen} onOpenChange={(v) => { setEditOpen(v); if (!v) setEditForm(null); }}>
        <DialogContent data-testid="modal-editar-usuario">
          <DialogHeader><DialogTitle>Editar usuário</DialogTitle></DialogHeader>
          {editForm && (
            <div className="space-y-3">
              <div>
                <Label>Nome</Label>
                <Input
                  value={editForm.nome}
                  onChange={(e) => setEditForm({ ...editForm, nome: e.target.value })}
                  data-testid="edit-user-nome"
                />
              </div>
              <div>
                <Label>E-mail</Label>
                <Input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  data-testid="edit-user-email"
                />
              </div>
              <div>
                <Label>Nova senha <span className="text-slate-400 text-xs">(deixe em branco para não alterar)</span></Label>
                <Input
                  type="password"
                  value={editForm.password}
                  onChange={(e) => setEditForm({ ...editForm, password: e.target.value })}
                  placeholder="mínimo 6 caracteres"
                  data-testid="edit-user-password"
                />
              </div>
              <div>
                <Label>Papel</Label>
                <Select
                  value={editForm.role}
                  onValueChange={(v) => setEditForm({ ...editForm, role: v })}
                >
                  <SelectTrigger data-testid="edit-user-role"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="vendedora">Vendedora</SelectItem>
                    <SelectItem value="supervisao">Supervisão</SelectItem>
                    <SelectItem value="diretoria">Diretoria</SelectItem>
                    <SelectItem value="fiscal">Fiscal</SelectItem>
                    <SelectItem value="analista_mkt">Analista de MKT</SelectItem>
                    <SelectItem value="gestao_cel">Gestão CeL Consultoria</SelectItem>
                    <SelectItem value="indicador">Indicador (parceiro externo)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Núcleo</Label>
                <Select
                  value={editForm.nucleo_id || ""}
                  onValueChange={(v) => setEditForm({ ...editForm, nucleo_id: v })}
                >
                  <SelectTrigger><SelectValue placeholder="Nenhum" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— Nenhum —</SelectItem>
                    {nucleos.map((n) => (<SelectItem key={n.id} value={n.id}>{n.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Código <span className="text-slate-400 text-xs">(usado pra bater com planilhas de importação)</span></Label>
                <Input
                  value={editForm.codigo}
                  onChange={(e) => setEditForm({ ...editForm, codigo: e.target.value })}
                  placeholder="ex: V001 ou 1"
                  data-testid="edit-user-codigo"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Cancelar</Button>
            <Button onClick={salvarEdicao} data-testid="btn-salvar-edicao-usuario">Salvar alterações</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </Card>
  );
}

function CursosTab() {
  const [cursos, setCursos] = useState([]);
  const [nucleos, setNucleos] = useState([]);
  const [emissores, setEmissores] = useState([]);
  const [users, setUsers] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editandoId, setEditandoId] = useState(null); // id do curso em edição (null = novo)
  const emptyForm = {
    nome: "", nucleo_id: "", mes_ano: "", data_realizacao: "",
    meta_inscritos: 0, meta_receita: 0, status: "ativo",
    emissor_id: "", local_realizacao: "", valor_cheio: 0,
    professor: "", carga_horaria_total: "", data_inicio: "", data_fim: "",
    diretor_nucleo_id: "", uf: "",
  };
  const [form, setForm] = useState(emptyForm);

  const load = () => api.get("/cursos").then((r) => setCursos(r.data));
  useEffect(() => {
    load();
    api.get("/nucleos").then((r) => setNucleos(r.data));
    api.get("/emissores").then((r) => setEmissores(r.data));
    api.get("/users").then((r) => setUsers((r.data || []).filter((u) => ["diretoria", "supervisao"].includes(u.role)))).catch(() => {});
  }, []);

  const abrirNovo = () => {
    setEditandoId(null);
    setForm(emptyForm);
    setDlg(true);
  };
  const abrirEditar = (c) => {
    setEditandoId(c.id);
    setForm({ ...emptyForm, ...c });
    setDlg(true);
  };

  const salvar = async () => {
    try {
      const payload = {
        ...form,
        meta_inscritos: Number(form.meta_inscritos) || 0,
        meta_receita: Number(form.meta_receita) || 0,
        valor_cheio: Number(form.valor_cheio) || 0,
        emissor_id: form.emissor_id || null,
        diretor_nucleo_id: form.diretor_nucleo_id || null,
      };
      if (editandoId) {
        await api.patch(`/cursos/${editandoId}`, payload);
        toast.success("Curso atualizado");
      } else {
        await api.post("/cursos", payload);
        toast.success("Curso criado");
      }
      setDlg(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const excluir = async (c) => {
    if (!window.confirm(`Excluir curso "${c.nome}"?\n\nBloqueado se houver inscrições ativas.`)) return;
    try {
      await api.delete(`/cursos/${c.id}`);
      toast.success("Curso excluído");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <Card className="p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl">Cursos</h2>
        <Button onClick={abrirNovo} data-testid="btn-novo-curso"><Plus className="w-4 h-4 mr-1" /> Novo</Button>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
            <th className="pb-3 pr-3">Nome</th><th className="pb-3 pr-3">Núcleo</th>
            <th className="pb-3 pr-3">Mês</th><th className="pb-3 pr-3">Datas</th>
            <th className="pb-3 pr-3">Professor</th>
            <th className="pb-3 pr-3 text-right">Meta insc.</th>
            <th className="pb-3 pr-3 text-right">Meta R$</th>
            <th className="pb-3 text-right w-32">Ações</th>
          </tr>
        </thead>
        <tbody>
          {cursos.map((c) => (
            <tr key={c.id} className="border-b border-slate-100" data-testid={`curso-row-${c.id}`}>
              <td className="py-2 pr-3 font-medium">{c.nome}</td>
              <td className="py-2 pr-3">{nucleos.find((n) => n.id === c.nucleo_id)?.nome}</td>
              <td className="py-2 pr-3">{c.mes_ano}</td>
              <td className="py-2 pr-3">{c.data_realizacao}</td>
              <td className="py-2 pr-3 text-slate-500 text-xs">{c.professor || "—"}</td>
              <td className="py-2 pr-3 text-right">{c.meta_inscritos}</td>
              <td className="py-2 pr-3 text-right tabular-nums">{formatBRL(c.meta_receita)}</td>
              <td className="py-2 text-right whitespace-nowrap">
                <button
                  onClick={() => abrirEditar(c)}
                  className="text-blue-600 hover:text-blue-800 text-xs font-semibold mr-2"
                  data-testid={`btn-editar-curso-${c.id}`}
                >
                  ✏️ Editar
                </button>
                <button
                  onClick={() => excluir(c)}
                  className="text-red-600 hover:text-red-800 text-xs font-semibold"
                  data-testid={`btn-excluir-curso-admin-${c.id}`}
                >
                  🗑️
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editandoId ? "Editar curso" : "Novo curso"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Nome *</Label><Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="curso-nome" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Núcleo *</Label>
                <Select value={form.nucleo_id} onValueChange={(v) => setForm({ ...form, nucleo_id: v })}>
                  <SelectTrigger data-testid="curso-nucleo"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>{nucleos.map((n) => (<SelectItem key={n.id} value={n.id}>{n.nome}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>UF de realização *</Label>
                <UfCursoSelect value={form.uf} onChange={(v) => setForm({ ...form, uf: v })} testid="curso-uf" />
              </div>
            </div>
            <div>
              <Label>Professor / Instrutor *</Label>
              <Input value={form.professor || ""} onChange={(e) => setForm({ ...form, professor: e.target.value })} placeholder="Ex: Prof. Dr. Silvio Almeida" data-testid="curso-professor" />
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <Label>Data início *</Label>
                <Input type="date" value={form.data_inicio || ""} onChange={(e) => setForm({ ...form, data_inicio: e.target.value })} data-testid="curso-inicio" />
              </div>
              <div>
                <Label>Data término *</Label>
                <Input type="date" value={form.data_fim || ""} onChange={(e) => setForm({ ...form, data_fim: e.target.value })} data-testid="curso-fim" />
              </div>
              <div>
                <Label>Dias (texto)</Label>
                <Input value={form.data_realizacao} placeholder="Ex: 20 a 22" onChange={(e) => setForm({ ...form, data_realizacao: e.target.value })} data-testid="curso-dias" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Carga horária *</Label>
                <Input value={form.carga_horaria_total || ""} onChange={(e) => setForm({ ...form, carga_horaria_total: e.target.value })} placeholder="Ex: 32 horas" data-testid="curso-carga" />
              </div>
              <div>
                <Label>Valor cheio (R$) *</Label>
                <Input type="number" step="0.01" value={form.valor_cheio || ""} onChange={(e) => setForm({ ...form, valor_cheio: e.target.value })} data-testid="curso-valor-cheio" placeholder="890.00" />
              </div>
            </div>
            <div>
              <Label>Cidade de realização {form.uf === "Online" ? "(opcional)" : "*"}</Label>
              <Input value={form.local_realizacao || ""} onChange={(e) => setForm({ ...form, local_realizacao: e.target.value })} placeholder="Ex: Brasília/DF · Hotel Nacional" data-testid="curso-local" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>PJ Emissora (CNPJ) *</Label>
                <Select value={form.emissor_id || ""} onValueChange={(v) => setForm({ ...form, emissor_id: v })}>
                  <SelectTrigger data-testid="curso-emissor"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    {emissores.map((e) => (<SelectItem key={e.id} value={e.id}>{e.razao_social} · {e.cnpj}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Diretor de Núcleo *</Label>
                <Select value={form.diretor_nucleo_id || ""} onValueChange={(v) => setForm({ ...form, diretor_nucleo_id: v })}>
                  <SelectTrigger data-testid="curso-diretor"><SelectValue placeholder="Selecionar" /></SelectTrigger>
                  <SelectContent>
                    {users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.nome}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Meta inscritos *</Label>
                <Input type="number" value={form.meta_inscritos} onChange={(e) => setForm({ ...form, meta_inscritos: e.target.value })} data-testid="curso-meta-i" />
              </div>
              <div>
                <Label>Meta receita (R$) *</Label>
                <Input type="number" step="0.01" value={form.meta_receita} onChange={(e) => setForm({ ...form, meta_receita: e.target.value })} data-testid="curso-meta-r" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button onClick={salvar} data-testid="curso-salvar">{editandoId ? "Salvar alterações" : "Criar curso"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function NucleosTab() {
  const [nucleos, setNucleos] = useState([]);
  const [nome, setNome] = useState("");
  const [editando, setEditando] = useState(null); // id do núcleo em edição
  const [form, setForm] = useState({ nome: "", diretor_nome: "", assinatura_url: "" });
  const [uploading, setUploading] = useState(null); // id do núcleo cujo upload está rodando
  const load = () => api.get("/nucleos").then((r) => setNucleos(r.data));
  useEffect(() => { load(); }, []);
  const criar = async () => {
    if (!nome.trim()) return;
    await api.post("/nucleos", { nome });
    setNome(""); load();
  };
  const excluir = async (id) => { if (window.confirm("Excluir?")) { await api.delete(`/nucleos/${id}`); load(); } };
  const abrirEditar = (n) => {
    setEditando(n.id);
    setForm({ nome: n.nome || "", diretor_nome: n.diretor_nome || "", assinatura_url: n.assinatura_url || "" });
  };
  const salvarEdicao = async () => {
    if (!form.nome.trim()) { toast.error("Nome é obrigatório"); return; }
    try {
      await api.patch(`/nucleos/${editando}`, {
        nome: form.nome.trim(),
        diretor_nome: form.diretor_nome?.trim() || null,
      });
      toast.success("Núcleo atualizado");
      setEditando(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const uploadAssinatura = async (nid, file) => {
    if (!file) return;
    setUploading(nid);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post(`/nucleos/${nid}/assinatura`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Assinatura enviada");
      setForm((f) => ({ ...f, assinatura_url: r.data.assinatura_url }));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro no upload");
    } finally { setUploading(null); }
  };
  return (
    <Card className="p-6 rounded-2xl">
      <h2 className="font-display font-bold text-xl mb-4">Núcleos</h2>
      <div className="flex gap-2 mb-4">
        <Input value={nome} onChange={(e) => setNome(e.target.value)} placeholder="Nome do núcleo" data-testid="nucleo-nome" />
        <Button onClick={criar} data-testid="btn-novo-nucleo">Adicionar</Button>
      </div>
      <ul className="space-y-3">
        {nucleos.map((n) => (
          <li key={n.id} className="border rounded-xl p-3 bg-slate-50/40" data-testid={`nucleo-row-${n.id}`}>
            {editando === n.id ? (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Nome do núcleo</Label>
                    <Input value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid={`edit-nucleo-nome-${n.id}`} />
                  </div>
                  <div>
                    <Label className="text-xs">Diretor(a) do núcleo</Label>
                    <Input value={form.diretor_nome} onChange={(e) => setForm({ ...form, diretor_nome: e.target.value })} placeholder="Ex: Dr. José Silva" data-testid={`edit-nucleo-diretor-${n.id}`} />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Assinatura (PNG/JPG)</Label>
                  <div className="flex items-center gap-2">
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={(e) => uploadAssinatura(n.id, e.target.files?.[0])}
                      className="text-xs"
                      data-testid={`edit-nucleo-assinatura-file-${n.id}`}
                    />
                    {uploading === n.id && <span className="text-xs text-blue-600">Enviando…</span>}
                    {form.assinatura_url && (
                      <img
                        src={form.assinatura_url.startsWith("http") ? form.assinatura_url : `${process.env.REACT_APP_BACKEND_URL}${form.assinatura_url}`}
                        alt="Assinatura"
                        className="h-10 bg-white border rounded px-2"
                      />
                    )}
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditando(null)}>Cancelar</Button>
                  <Button size="sm" onClick={salvarEdicao} data-testid={`btn-salvar-nucleo-${n.id}`}>Salvar</Button>
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between gap-2">
                <div className="flex-1">
                  <div className="font-bold">{n.nome}</div>
                  <div className="text-xs text-slate-500 flex items-center gap-3 mt-0.5">
                    <span>Diretor(a): <b>{n.diretor_nome || "—"}</b></span>
                    {n.assinatura_url && (
                      <span className="text-emerald-600 flex items-center gap-1">
                        ✓ Assinatura cadastrada
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={() => abrirEditar(n)}
                  className="text-blue-600 hover:text-blue-800 text-sm font-semibold px-2"
                  data-testid={`btn-editar-nucleo-${n.id}`}
                >
                  ✏️ Editar
                </button>
                <button onClick={() => excluir(n.id)} className="text-red-500" data-testid={`btn-excluir-nucleo-${n.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            )}
          </li>
        ))}
      </ul>
    </Card>
  );
}

function ImportarTab() {
  const [txt, setTxt] = useState("");
  const [resultado, setResultado] = useState(null);

  const importar = async () => {
    const linhas = txt.split("\n").map((row) => row.split("\t"))
      .filter((cols) => cols[0]?.trim())
      .map((cols) => ({
        municipio: cols[0], uf: cols[1] || "",
        orgao: cols[2] || "Câmara",
        pessoa: cols[3] || "", funcao: cols[4] || "Outro",
        telefone: cols[5] || "", vendedora_email: cols[6] || "",
      }));
    if (linhas.length === 0) { toast.error("Nenhuma linha detectada"); return; }
    try {
      const { data } = await api.post("/importar", { linhas });
      setResultado(data);
      toast.success(`Importado! ${data.municipios} municípios, ${data.pessoas} pessoas.`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <Card className="p-6 rounded-2xl">
      <h2 className="font-display font-bold text-xl mb-2">Importar planilha</h2>
      <p className="text-sm text-slate-500 mb-3">
        Cole células copiadas do Excel/Sheets (uma linha por município), separadas por TAB.<br />
        Colunas: <b>Município | UF | Órgão | Nome Pessoa | Função | Telefone | E-mail Vendedora</b>
      </p>
      <Textarea rows={10} value={txt} onChange={(e) => setTxt(e.target.value)} placeholder="Piratuba	SC	Câmara	Maria Santos	Vereador	(49)99999-0000	juliana@ceap.com.br" data-testid="importar-txt" />
      <div className="mt-3 flex gap-2">
        <Button onClick={importar} data-testid="btn-importar"><Upload className="w-4 h-4 mr-1" /> Importar</Button>
        {resultado && <div className="text-sm self-center">{resultado.municipios} novos municípios · {resultado.pessoas} novas pessoas</div>}
      </div>
    </Card>
  );
}

function RestaurarCarteiraTab() {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [dryResult, setDryResult] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // Iter 58.6: limpar inscrições órfãs
  const [orfasResult, setOrfasResult] = useState(null);
  const [orfasBusy, setOrfasBusy] = useState(false);
  const [orfasConfirmOpen, setOrfasConfirmOpen] = useState(false);

  const simularOrfas = async () => {
    setOrfasBusy(true); setOrfasResult(null);
    try {
      const { data } = await api.post("/admin/limpar-inscricoes-orfas?dry_run=true");
      setOrfasResult(data);
      toast.success(`Encontrado: ${data.orfas_encontradas || 0} inscrição(ões) órfã(s)`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro");
    } finally { setOrfasBusy(false); }
  };
  const efetivarOrfas = async () => {
    setOrfasConfirmOpen(false); setOrfasBusy(true);
    try {
      const { data } = await api.post("/admin/limpar-inscricoes-orfas?dry_run=false");
      setOrfasResult(data);
      toast.success(`${data.canceladas} inscrição(ões) órfã(s) canceladas. Realizado corrigido.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao aplicar");
    } finally { setOrfasBusy(false); }
  };
  const brl = (n) => Number(n || 0).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

  const simular = async () => {
    if (!file) { toast.error("Selecione o arquivo .xlsx"); return; }
    setBusy(true); setDryResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/restaurar-carteira-planilha?dry_run=true", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 120000,
      });
      setDryResult(data);
      toast.success("Simulação concluída — revise os números antes de confirmar");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro na simulação");
    } finally { setBusy(false); }
  };

  const efetivar = async () => {
    setConfirmOpen(false);
    if (!file) { toast.error("Selecione o arquivo .xlsx"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/restaurar-carteira-planilha?dry_run=false", fd, {
        headers: { "Content-Type": "multipart/form-data" }, timeout: 300000,
      });
      setDryResult(data);
      toast.success(`Restauração concluída! ${data.stats.atualizados} pessoas atualizadas.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao aplicar");
    } finally { setBusy(false); }
  };

  return (
    <Card className="p-6 rounded-2xl" data-testid="restaurar-carteira-tab">
      <h2 className="font-display font-bold text-xl mb-2">Restaurar Carteira via Planilha</h2>
      <p className="text-sm text-slate-500 mb-4">
        Ferramenta cirúrgica: atualiza <b>somente</b> os campos <code>frequencia</code> e <code>categoria</code> de pessoas
        já existentes no CRM, com base numa planilha de referência. Contatos, cards, inscrições, interações e demais dados
        <b> NÃO SÃO TOCADOS</b>.
      </p>
      <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-xs text-slate-600">
        <b>Formato do arquivo:</b> .xlsx com 2 abas obrigatórias:
        <div className="mt-1"><b>Municipios</b>: colunas <code>codigo, municipio, estado</code></div>
        <div><b>Pessoas</b>: colunas <code>codigo_municipio, nome, frequencia</code> (0=Não Cliente · 1=Consumidor · 2+=Cliente)</div>
      </div>
      <div className="flex items-center gap-3">
        <Input type="file" accept=".xlsx"
          onChange={(e) => { setFile(e.target.files?.[0] || null); setDryResult(null); }}
          data-testid="restaurar-file" />
        <Button onClick={simular} disabled={!file || busy} variant="outline" data-testid="btn-simular-restauracao">
          {busy && dryResult === null ? "Simulando..." : "Simular (dry-run)"}
        </Button>
      </div>

      {dryResult && (
        <div className="mt-5 space-y-3">
          <div className={`rounded-lg p-4 border ${dryResult.dry_run ? "bg-amber-50 border-amber-300" : "bg-green-50 border-green-300"}`}>
            <div className="font-bold text-sm mb-2">
              {dryResult.dry_run ? "🧪 Simulação (nada salvo ainda)" : "✅ Restauração aplicada"}
            </div>
            <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-xs">
              <div>Linhas no template: <b>{dryResult.stats.linhas_template}</b></div>
              <div>Serão atualizados: <b className="text-green-700">{dryResult.stats.atualizados}</b></div>
              <div>Município não encontrado: <b>{dryResult.stats.municipio_nao_encontrado}</b></div>
              <div>Pessoa não encontrada: <b>{dryResult.stats.pessoa_nao_encontrada}</b></div>
              <div>Pessoa ambígua (skip): <b>{dryResult.stats.pessoa_ambigua}</b></div>
              <div>Municípios casados: <b>{dryResult.municipios_casados_no_db}/{dryResult.municipios_do_template}</b></div>
            </div>
            <div className="mt-3 flex gap-4 text-xs">
              <div>→ Cliente: <b className="text-emerald-700">{dryResult.stats.por_categoria.cliente}</b></div>
              <div>→ Consumidor: <b className="text-blue-700">{dryResult.stats.por_categoria.consumidor}</b></div>
              <div>→ Não Cliente: <b className="text-slate-500">{dryResult.stats.por_categoria.nao_cliente}</b></div>
            </div>
          </div>

          {dryResult.exemplos_nao_encontrados?.length > 0 && (
            <details className="text-xs bg-red-50 border border-red-200 rounded p-3">
              <summary className="cursor-pointer font-bold text-red-800">
                {dryResult.stats.pessoa_nao_encontrada} pessoas não encontradas (amostra)
              </summary>
              <ul className="mt-2 space-y-0.5 text-red-700">
                {dryResult.exemplos_nao_encontrados.map((x, i) => <li key={i}>· {x}</li>)}
              </ul>
            </details>
          )}
          {dryResult.exemplos_ambiguos?.length > 0 && (
            <details className="text-xs bg-yellow-50 border border-yellow-200 rounded p-3">
              <summary className="cursor-pointer font-bold text-yellow-800">
                {dryResult.stats.pessoa_ambigua} ambíguos (múltiplos com mesmo nome no município)
              </summary>
              <ul className="mt-2 space-y-0.5 text-yellow-700">
                {dryResult.exemplos_ambiguos.map((x, i) => <li key={i}>· {x}</li>)}
              </ul>
            </details>
          )}
          {dryResult.municipios_nao_encontrados_amostra?.length > 0 && (
            <details className="text-xs bg-orange-50 border border-orange-200 rounded p-3">
              <summary className="cursor-pointer font-bold text-orange-800">
                Municípios do template não encontrados no CRM (amostra)
              </summary>
              <ul className="mt-2 space-y-0.5 text-orange-700">
                {dryResult.municipios_nao_encontrados_amostra.map((x, i) => <li key={i}>· {x}</li>)}
              </ul>
            </details>
          )}

          {dryResult.dry_run && dryResult.stats.atualizados > 0 && (
            <div className="pt-2">
              <Button onClick={() => setConfirmOpen(true)} disabled={busy}
                className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-efetivar-restauracao">
                Aplicar restauração ({dryResult.stats.atualizados} pessoas)
              </Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar restauração</DialogTitle>
          </DialogHeader>
          <p className="text-sm">Vou atualizar <b>frequencia</b> e <b>categoria</b> em <b>{dryResult?.stats?.atualizados || 0}</b> pessoas. Nada mais é tocado. Confirma?</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>Cancelar</Button>
            <Button onClick={efetivar} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="btn-confirmar-restauracao">
              Sim, aplicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Iter 58.6: Bloco separado — Limpar inscrições órfãs no Realizado */}
      <div className="mt-8 pt-6 border-t border-slate-200">
        <h2 className="font-display font-bold text-xl mb-2">Limpar inscrições órfãs no Realizado</h2>
        <p className="text-sm text-slate-500 mb-4">
          Corrige o bug antigo em que ao arrastar um card de <b>Inscritos</b> para outro estágio (ex: Negativas),
          as inscrições correspondentes não eram canceladas — deixando o Realizado inflado com receita e alunos
          que na verdade não fecharam. Esta rotina identifica e cancela todas as inscrições cujo card já saiu
          de "Inscritos". O bug em si foi corrigido no drag-and-drop; esta ferramenta só é necessária para
          limpar o histórico anterior à correção.
        </p>
        <div className="flex gap-2 items-center">
          <Button onClick={simularOrfas} disabled={orfasBusy} variant="outline" data-testid="btn-simular-orfas">
            {orfasBusy && !orfasResult ? "Verificando..." : "Simular limpeza"}
          </Button>
        </div>

        {orfasResult && (
          <div className="mt-4 space-y-3">
            <div className={`rounded-lg p-4 border ${orfasResult.dry_run ? "bg-amber-50 border-amber-300" : "bg-green-50 border-green-300"}`}>
              <div className="font-bold text-sm mb-2">
                {orfasResult.dry_run ? "🧪 Simulação (nada salvo ainda)" : "✅ Limpeza aplicada"}
              </div>
              <div className="grid grid-cols-3 gap-4 text-xs">
                <div>Inscrições órfãs: <b className="text-red-700">{orfasResult.orfas_encontradas ?? orfasResult.canceladas}</b></div>
                <div>Valor a devolver: <b className="text-red-700">{brl(orfasResult.valor_devolvido)}</b></div>
                <div>Alunos a devolver: <b className="text-red-700">{orfasResult.quantidade_devolvida}</b></div>
              </div>
              {orfasResult.obs && <div className="mt-2 text-xs text-slate-600">{orfasResult.obs}</div>}
            </div>
            {orfasResult.exemplos?.length > 0 && (
              <details className="text-xs bg-slate-50 border border-slate-200 rounded p-3">
                <summary className="cursor-pointer font-bold text-slate-700">
                  Ver amostra de {orfasResult.exemplos.length} inscrição(ões)
                </summary>
                <ul className="mt-2 space-y-1 text-slate-600">
                  {orfasResult.exemplos.map((x, i) => (
                    <li key={i}>· Card em <b>{x.estagio_atual_card}</b> · qtd={x.quantidade} · {brl(x.valor_vendido)}</li>
                  ))}
                </ul>
              </details>
            )}
            {orfasResult.dry_run && (orfasResult.orfas_encontradas || 0) > 0 && (
              <Button onClick={() => setOrfasConfirmOpen(true)} disabled={orfasBusy}
                className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-efetivar-orfas">
                Aplicar limpeza ({orfasResult.orfas_encontradas} inscrições)
              </Button>
            )}
          </div>
        )}

        <Dialog open={orfasConfirmOpen} onOpenChange={setOrfasConfirmOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirmar limpeza de inscrições órfãs</DialogTitle>
            </DialogHeader>
            <p className="text-sm">
              Vou cancelar <b>{orfasResult?.orfas_encontradas || 0}</b> inscrição(ões) que estão em cards fora do estágio "Inscritos".
              Isso vai <b>reduzir o Realizado</b> em <b>{brl(orfasResult?.valor_devolvido)}</b> e devolver{" "}
              <b>{orfasResult?.quantidade_devolvida}</b> alunos ao total correto. Confirma?
            </p>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOrfasConfirmOpen(false)}>Cancelar</Button>
              <Button onClick={efetivarOrfas} className="bg-red-600 hover:bg-red-700 text-white" data-testid="btn-confirmar-orfas">
                Sim, cancelar essas inscrições
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Card>
  );
}

function CarteiraTab() {
  // Removido: "Popular funil com carteiras" — não é mais usado
  return null;
}

function MetasMensaisTab() {
  const [users, setUsers] = useState([]);
  const [mesAno, setMesAno] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
  });
  const [metas, setMetas] = useState([]);
  const [edits, setEdits] = useState({});

  const load = async () => {
    const [u, m] = await Promise.all([
      api.get("/users"),
      api.get(`/metas-mensais?mes_ano=${mesAno}`),
    ]);
    setUsers((u.data || []).filter((x) => x.role === "vendedora"));
    setMetas(m.data || []);
    const map = {};
    for (const md of m.data || []) {
      map[md.vendedora_id] = { meta_receita: md.meta_receita, meta_inscritos: md.meta_inscritos };
    }
    setEdits(map);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [mesAno]);

  const salvar = async (vend) => {
    const e = edits[vend.id] || { meta_receita: 0, meta_inscritos: 0 };
    try {
      await api.post("/metas-mensais", {
        vendedora_id: vend.id,
        mes_ano: mesAno,
        meta_receita: Number(e.meta_receita || 0),
        meta_inscritos: Number(e.meta_inscritos || 0),
      });
      toast.success(`Meta de ${vend.nome} salva`);
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Erro"); }
  };

  const remover = async (vend) => {
    const found = metas.find((m) => m.vendedora_id === vend.id);
    if (!found) return;
    if (!window.confirm(`Remover meta de ${vend.nome}?`)) return;
    await api.delete(`/metas-mensais/${found.id}`);
    load();
  };

  const meses = [];
  {
    const now = new Date();
    for (let i = -1; i <= 6; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() + i, 1);
      meses.push(`${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`);
    }
  }

  return (
    <Card className="p-6 rounded-2xl" data-testid="metas-mensais-tab">
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h2 className="font-display font-bold text-xl">Metas mensais por vendedora</h2>
          <p className="text-sm text-slate-500">Cadastre a meta de receita e de inscritos de cada vendedora para o mês selecionado.</p>
        </div>
        <div>
          <Label className="text-xs uppercase text-slate-500 tracking-widest">Mês / Ano</Label>
          <Select value={mesAno} onValueChange={setMesAno}>
            <SelectTrigger className="w-40" data-testid="metas-mes-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {meses.map((m) => (<SelectItem key={m} value={m}>{m}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
            <th className="pb-3 pr-3">Vendedora</th>
            <th className="pb-3 pr-3">Núcleo</th>
            <th className="pb-3 pr-3">Meta Receita (R$)</th>
            <th className="pb-3 pr-3">Meta Inscritos</th>
            <th className="pb-3"></th>
          </tr>
        </thead>
        <tbody>
          {users.map((u) => {
            const e = edits[u.id] || {};
            const found = metas.find((m) => m.vendedora_id === u.id);
            return (
              <tr key={u.id} className="border-b border-slate-100" data-testid={`meta-row-${u.id}`}>
                <td className="py-2 pr-3 font-medium">{u.nome}</td>
                <td className="py-2 pr-3 text-slate-500 text-xs">{u.nucleo_nome || "—"}</td>
                <td className="py-2 pr-3">
                  <Input
                    type="number"
                    value={e.meta_receita ?? ""}
                    onChange={(ev) => setEdits({ ...edits, [u.id]: { ...e, meta_receita: ev.target.value } })}
                    className="w-32"
                    data-testid={`meta-receita-${u.id}`}
                  />
                </td>
                <td className="py-2 pr-3">
                  <Input
                    type="number"
                    value={e.meta_inscritos ?? ""}
                    onChange={(ev) => setEdits({ ...edits, [u.id]: { ...e, meta_inscritos: ev.target.value } })}
                    className="w-24"
                    data-testid={`meta-inscritos-${u.id}`}
                  />
                </td>
                <td className="py-2 flex gap-1">
                  <Button size="sm" onClick={() => salvar(u)} data-testid={`meta-save-${u.id}`}>Salvar</Button>
                  {found && <Button size="sm" variant="outline" onClick={() => remover(u)} className="text-red-600 border-red-200">Remover</Button>}
                </td>
              </tr>
            );
          })}
          {users.length === 0 && <tr><td colSpan={5} className="text-slate-400 py-4 text-center">Nenhuma vendedora cadastrada.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function EmissoresTab() {
  const [items, setItems] = useState([]);
  const [dlg, setDlg] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const empty = { razao_social: "", cnpj: "", endereco: "", email: "", telefone: "", banco: "", agencia: "", conta: "", pix: "", tipo_pix: "", ativo: true };
  const [form, setForm] = useState(empty);
  const load = () => api.get("/emissores").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const abrirNovo = () => { setEditandoId(null); setForm(empty); setDlg(true); };
  const abrirEditar = (e) => { setEditandoId(e.id); setForm({ ...empty, ...e }); setDlg(true); };
  const salvar = async () => {
    try {
      if (editandoId) {
        await api.patch(`/emissores/${editandoId}`, form);
        toast.success("Emissor atualizado");
      } else {
        await api.post("/emissores", form);
        toast.success("Emissor cadastrado");
      }
      setDlg(false); setForm(empty); setEditandoId(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };
  const excluir = async (id) => { if (window.confirm("Excluir emissor?")) { await api.delete(`/emissores/${id}`); load(); } };
  return (
    <Card className="p-6 rounded-2xl">
      <div className="flex items-center justify-between mb-4">
        <h2 className="font-display font-bold text-xl">Emissores (CNPJs para propostas)</h2>
        <Button onClick={abrirNovo} data-testid="btn-novo-emissor"><Plus className="w-4 h-4 mr-1" /> Novo CNPJ</Button>
      </div>
      <p className="text-sm text-slate-500 mb-3">Estes CNPJs aparecem para as vendedoras selecionarem ao gerar propostas comerciais e no envio de e-mail de confirmação de inscrição.</p>
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-xs uppercase tracking-widest text-slate-500 border-b">
            <th className="pb-3 pr-3">Razão Social</th><th className="pb-3 pr-3">CNPJ</th>
            <th className="pb-3 pr-3">Banco / PIX</th><th className="pb-3 pr-3">E-mail</th>
            <th className="pb-3 text-right w-32">Ações</th>
          </tr>
        </thead>
        <tbody>
          {items.map((e) => (
            <tr key={e.id} className="border-b border-slate-100" data-testid={`emissor-row-${e.id}`}>
              <td className="py-2 pr-3 font-medium">{e.razao_social}</td>
              <td className="py-2 pr-3">{e.cnpj}</td>
              <td className="py-2 pr-3 text-slate-500 text-xs">
                {e.banco && <div>{e.banco} · Ag {e.agencia || "—"} · Cc {e.conta || "—"}</div>}
                {e.pix && <div>PIX: {e.pix} {e.tipo_pix && `(${e.tipo_pix})`}</div>}
                {!e.banco && !e.pix && "—"}
              </td>
              <td className="py-2 pr-3 text-slate-500 text-xs">{e.email || "—"}</td>
              <td className="py-2 text-right whitespace-nowrap">
                <button
                  onClick={() => abrirEditar(e)}
                  className="text-blue-600 hover:text-blue-800 text-xs font-semibold mr-2"
                  data-testid={`btn-editar-emissor-${e.id}`}
                >
                  ✏️ Editar
                </button>
                <button onClick={() => excluir(e.id)} className="text-red-500 hover:text-red-700" data-testid={`btn-excluir-emissor-${e.id}`}>
                  <Trash2 className="w-4 h-4 inline" />
                </button>
              </td>
            </tr>
          ))}
          {items.length === 0 && <tr><td colSpan={5} className="text-slate-400 py-4 text-center">Nenhum emissor cadastrado.</td></tr>}
        </tbody>
      </table>
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="max-w-2xl">
          <DialogHeader><DialogTitle>{editandoId ? "Editar Emissor" : "Novo Emissor de CNPJ"}</DialogTitle></DialogHeader>
          <div className="space-y-3 max-h-[70vh] overflow-y-auto pr-1">
            <div><Label>Razão Social *</Label><Input value={form.razao_social} onChange={(e) => setForm({ ...form, razao_social: e.target.value })} data-testid="em-razao" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>CNPJ *</Label><Input value={form.cnpj} onChange={(e) => setForm({ ...form, cnpj: e.target.value })} placeholder="00.000.000/0000-00" data-testid="em-cnpj" /></div>
              <div><Label>Telefone</Label><Input value={form.telefone} onChange={(e) => setForm({ ...form, telefone: e.target.value })} data-testid="em-telefone" /></div>
            </div>
            <div><Label>Endereço</Label><Input value={form.endereco} onChange={(e) => setForm({ ...form, endereco: e.target.value })} data-testid="em-endereco" /></div>
            <div><Label>E-mail</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="em-email" /></div>

            <div className="pt-3 mt-2 border-t border-slate-200">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold mb-2">Dados bancários (para confirmação de inscrição)</div>
              <div className="grid grid-cols-3 gap-3">
                <div><Label>Banco</Label><Input value={form.banco} onChange={(e) => setForm({ ...form, banco: e.target.value })} placeholder="Ex: Banco do Brasil" data-testid="em-banco" /></div>
                <div><Label>Agência</Label><Input value={form.agencia} onChange={(e) => setForm({ ...form, agencia: e.target.value })} placeholder="0000" data-testid="em-agencia" /></div>
                <div><Label>Conta</Label><Input value={form.conta} onChange={(e) => setForm({ ...form, conta: e.target.value })} placeholder="00000-0" data-testid="em-conta" /></div>
              </div>
              <div className="grid grid-cols-3 gap-3 mt-3">
                <div className="col-span-2"><Label>Chave PIX</Label><Input value={form.pix} onChange={(e) => setForm({ ...form, pix: e.target.value })} placeholder="CNPJ, e-mail, celular, chave aleatória" data-testid="em-pix" /></div>
                <div>
                  <Label>Tipo PIX</Label>
                  <Select value={form.tipo_pix} onValueChange={(v) => setForm({ ...form, tipo_pix: v })}>
                    <SelectTrigger data-testid="em-tipo-pix"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {["CNPJ", "E-mail", "Celular", "Aleatória"].map(t => (<SelectItem key={t} value={t}>{t}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>Cancelar</Button>
            <Button onClick={salvar} data-testid="em-salvar">Salvar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

export default function Admin() {
  return (
    <div className="space-y-6" data-testid="admin-page">
      <div>
        <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900">Administração</h1>
        <p className="text-slate-500 text-sm">Usuários, cursos, núcleos e importações</p>
      </div>
      <Tabs defaultValue="usuarios">
        <TabsList>
          <TabsTrigger value="usuarios" data-testid="tab-usuarios">Usuários</TabsTrigger>
          <TabsTrigger value="cursos" data-testid="tab-cursos">Cursos</TabsTrigger>
          <TabsTrigger value="nucleos" data-testid="tab-nucleos">Núcleos</TabsTrigger>
          <TabsTrigger value="metas" data-testid="tab-metas">Metas Mensais</TabsTrigger>
          <TabsTrigger value="emissores" data-testid="tab-emissores">Emissores CNPJ</TabsTrigger>
          <TabsTrigger value="importar" data-testid="tab-importar">Importar Planilha</TabsTrigger>
          <TabsTrigger value="restaurar" data-testid="tab-restaurar">Restaurar Carteira</TabsTrigger>
        </TabsList>
        <TabsContent value="usuarios" className="mt-4"><UsuariosTab /></TabsContent>
        <TabsContent value="cursos" className="mt-4"><CursosTab /></TabsContent>
        <TabsContent value="nucleos" className="mt-4"><NucleosTab /></TabsContent>
        <TabsContent value="metas" className="mt-4"><MetasMensaisTab /></TabsContent>
        <TabsContent value="emissores" className="mt-4"><EmissoresTab /></TabsContent>
        <TabsContent value="importar" className="mt-4"><ImportarTab /></TabsContent>
        <TabsContent value="restaurar" className="mt-4"><RestaurarCarteiraTab /></TabsContent>
      </Tabs>
    </div>
  );
}
